import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  organizationInvitesApi,
  type BulkInviteResult,
  type OrganizationInviteData,
} from '@/lib/api/organization-invites.api';
import { queryKeys } from '../query-keys';
import { toast } from 'sonner';
import { memberApi } from '@/lib/api/members.api';
import { createLogger } from '@/lib/logger';

const logger = createLogger('hooks/mutations/useOrganizationInvitesMutations');

/**
 * Mutation hook for accepting an organization invite
 */
export function useAcceptInviteMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (token: string) => {
      return organizationInvitesApi.acceptInvite(token);
    },
    onSuccess: () => {
      // Invalidate invites queries
      queryClient.invalidateQueries({ queryKey: queryKeys.organizationInvites.lists() });
      
      // Invalidate organizations query
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.lists() });
      
      // Clear member cache
      memberApi.clearCache();
      
      // Invalidate members query
      queryClient.invalidateQueries({ queryKey: queryKeys.members.lists() });
    },
    onError: (error) => {
      logger.error('[useAcceptInviteMutation] Error accepting invite:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to accept invite');
    },
  });
}

/**
 * Mutation hook for declining an organization invite
 */
export function useDeclineInviteMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (token: string) => {
      return organizationInvitesApi.declineInvite(token);
    },
    onSuccess: () => {
      // Invalidate invites queries
      queryClient.invalidateQueries({ queryKey: queryKeys.organizationInvites.lists() });
      
      toast.success('Invite declined');
    },
    onError: (error) => {
      logger.error('[useDeclineInviteMutation] Error declining invite:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to decline invite');
    },
  });
}

/**
 * Mutation hook for inviting users to an organization
 */
export function useInviteUsersMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      invites
    }: {
      organizationId: string;
      invites: Array<{ email: string; role?: 'ADMIN' | 'MEMBER'; permissionSetId?: string; metadata?: Record<string, unknown> }>
    }) => {
      const results = await organizationInvitesApi.bulkCreate(organizationId, invites);
      const successes = results.filter((result): result is BulkInviteResult & { status: 'success' } => result.status === 'success');
      const failures = results.filter((result): result is BulkInviteResult & { status: 'error' } => result.status === 'error');

      if (successes.length === 0) {
        const summary = failures
          .slice(0, 3)
          .map(result => `${result.email}: ${result.error || 'Failed to send invite'}`)
          .join(' | ');
        throw new Error(summary || 'Failed to send invitations');
      }

      return {
        results,
        successes,
        failures,
      };
    },
    onSuccess: ({ successes, failures }) => {
      // Invalidate invites queries
      queryClient.invalidateQueries({ queryKey: queryKeys.organizationInvites.lists() });

      if (failures.length > 0) {
        const detail = failures
          .slice(0, 2)
          .map(result => `${result.email}: ${result.error || 'Failed'}`)
          .join(' | ');
        toast.warning(
          `${successes.length} invite${successes.length === 1 ? '' : 's'} sent, ${failures.length} failed`,
          { description: detail || undefined }
        );
        return;
      }

      toast.success('Invites sent successfully');
    },
    onError: (error) => {
      logger.error('[useInviteUsersMutation] Error inviting users:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to invite users');
    },
  });
}

/**
 * Mutation hook for resending an invite
 */
export function useResendInviteMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (inviteId: string) => {
      return organizationInvitesApi.resendInvite(inviteId);
    },
    onSuccess: (updatedInvite) => {
      // Update the invite in the cache
      queryClient.setQueryData(
        queryKeys.organizationInvites.list({ organizationId: updatedInvite.organizationId }),
        (old: { data: OrganizationInviteData[]; meta: { total: number } } | undefined) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.map(invite => invite.id === updatedInvite.id ? updatedInvite : invite)
          };
        }
      );
      
      // Invalidate to ensure consistency
      queryClient.invalidateQueries({ queryKey: queryKeys.organizationInvites.lists() });
      
      toast.success('Invite resent successfully');
    },
    onError: (error) => {
      logger.error('[useResendInviteMutation] Error resending invite:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to resend invite');
    },
  });
}

/**
 * Mutation hook for cancelling an invite
 */
export function useCancelInviteMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ inviteId }: { inviteId: string; organizationId?: string }) => {
      return organizationInvitesApi.cancelInvite(inviteId);
    },
    onSuccess: (_, { inviteId, organizationId }) => {
      // Remove from cache
      queryClient.setQueryData(
        queryKeys.organizationInvites.list({ organizationId }),
        (old: { data: OrganizationInviteData[]; meta: { total: number } } | undefined) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.filter(invite => invite.id !== inviteId),
            meta: { total: Math.max(0, old.meta.total - 1) }
          };
        }
      );
      
      // Invalidate to ensure consistency
      queryClient.invalidateQueries({ queryKey: queryKeys.organizationInvites.lists() });
      
      toast.success('Invite cancelled successfully');
    },
    onError: (error) => {
      logger.error('[useCancelInviteMutation] Error cancelling invite:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to cancel invite');
    },
  });
}
